import { Trans, useLingui } from '@lingui/react/macro'
import { Card, Group, Stack, Text, UnstyledButton } from '@mantine/core'
import { PhotoGallery } from '@markku/components'
import type {
  AttachmentResponse,
  MaintenanceLogWithRelations,
} from '@markku/types'
import { IconFile, IconFileTypePdf } from '@tabler/icons-react'

interface AttachmentsCardProps {
  log: MaintenanceLogWithRelations
}

const isImageAttachment = (attachment: AttachmentResponse): boolean => {
  if (attachment.mimeType?.startsWith('image/')) return true
  if (attachment.filename?.match(/\.(jpg|jpeg|png|gif|webp)$/i)) return true
  return false
}

const getFileIcon = (attachment: AttachmentResponse) => {
  if (
    attachment.mimeType === 'application/pdf' ||
    attachment.filename?.endsWith('.pdf')
  ) {
    return <IconFileTypePdf size={20} />
  }
  return <IconFile size={20} />
}

export function AttachmentsCard({ log }: AttachmentsCardProps) {
  const { t } = useLingui()
  const attachments = log.attachments || []

  if (attachments.length === 0) {
    return null
  }

  const photos = attachments.filter(isImageAttachment)
  const documents = attachments.filter((att) => !isImageAttachment(att))

  return (
    <Card withBorder>
      <Text size="lg" fw={600} mb="md">
        <Trans>Attachments</Trans>
      </Text>

      <Stack gap="lg">
        {/* Photos */}
        {photos.length > 0 && (
          <PhotoGallery photos={photos} label={t`Photos`} />
        )}

        {/* Other documents */}
        {documents.length > 0 && (
          <Stack gap="xs">
            <Text size="sm" fw={500}>
              <Trans>Documents</Trans>
            </Text>
            <Stack gap="xs">
              {documents.map((doc) => (
                <UnstyledButton
                  key={doc.id}
                  component="a"
                  href={doc.downloadUrl || undefined}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    padding: 'var(--mantine-spacing-xs)',
                    borderRadius: 'var(--mantine-radius-sm)',
                    border: '1px solid var(--mantine-color-gray-3)',
                    transition: 'background-color 0.2s',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor =
                      'var(--mantine-color-gray-0)'
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent'
                  }}
                >
                  <Group gap="sm">
                    {getFileIcon(doc)}
                    <Stack gap={0} style={{ flex: 1 }}>
                      <Text size="sm" fw={500}>
                        {doc.filename || <Trans>Unnamed file</Trans>}
                      </Text>
                      {doc.description && (
                        <Text size="xs" c="dimmed">
                          {doc.description}
                        </Text>
                      )}
                    </Stack>
                  </Group>
                </UnstyledButton>
              ))}
            </Stack>
          </Stack>
        )}
      </Stack>
    </Card>
  )
}
